import React, { useState, useMemo, useEffect } from 'react'
import { Dialog } from 'primereact/dialog'
import { ProgressBar } from 'primereact/progressbar'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import useAcompanhamentoItensSeparados from './AcompanhamentoItensSeparadosHook'
import './AcompanhamentoItensSeparados.css'

export default function AcompanhamentoItensSeparadosModal({ visible, onClose, pickingId }) {
  const { items, loading, manualRefresh, getAnimationClass } = useAcompanhamentoItensSeparados(
    pickingId,
    visible,
  )
  const [maximized, setMaximized] = useState(false)
  const [filterText, setFilterText] = useState('')

  useEffect(() => {
    if (!visible) {
      setFilterText('')
    }
  }, [visible])

  const filteredItems = useMemo(() => {
    if (!filterText.trim()) {
      return items
    }

    const searchTerm = filterText.toLowerCase().trim()
    return items.filter(item => {
      const productCode = (item.product_code || '').toLowerCase()
      const productName = (item.product_name || '').toLowerCase()
      const qteTotal = String(item.qte_total || '').toLowerCase()
      const qteSeparada = String(item.qte_separada || '').toLowerCase()
      const lastBip = (item.last_bip || '').toLowerCase()

      return (
        productCode.includes(searchTerm) ||
        productName.includes(searchTerm) ||
        qteTotal.includes(searchTerm) ||
        qteSeparada.includes(searchTerm) ||
        lastBip.includes(searchTerm)
      )
    })
  }, [items, filterText])

  return (
    <Dialog
      className="acomp-itens__modal"
      header="Acompanhamento Itens Separados"
      visible={visible}
      onHide={onClose}
      maximizable
      maximized={maximized}
      onMaximize={e => setMaximized(e.maximized)}
      style={maximized ? { width: '100vw', height: '100vh' } : { width: '88vw', height: '82vh' }}
      contentStyle={{
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
      }}
    >
      <div className="acomp-itens__container">
        <div className="acomp-itens__header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center'}}>
              <i className="pi pi-search" style={{ fontSize: '0.9rem', color: '#666' }} />
              <InputText
                type="text"
                placeholder="Filtrar por produto, quantidade, bip..."
                value={filterText}
                onChange={e => setFilterText(e.target.value)}
                style={{
                  fontSize: '0.8rem',
                  padding: '6px 10px',
                  border: '1px solid #ddd',
                  borderRadius: '3px',
                  width: '500px',
                }}
              />
            </div>
            <Button
              label="Atualizar"
              icon={`pi pi-refresh${loading ? ' pi-spin' : ''}`}
              onClick={manualRefresh}
              disabled={loading}
              style={{
                backgroundColor: '#14B0BF',
                border: 'none',
                color: 'white',
                fontSize: '0.8rem',
                padding: '8px 14px',
                marginLeft: '10px',
              }}
            />
          </div>
          <span className="acomp-itens__header-count">
            {filteredItems.length} {filteredItems.length === 1 ? 'produto' : 'produtos'} bipados
          </span>
        </div>

        <div className="acomp-itens__cards-wrapper">
          {filteredItems.length === 0 && !loading && (
            <div className="acomp-itens__empty-message">
              {items.length === 0
                ? 'Nenhum produto bipado ainda neste picking'
                : 'Nenhum produto encontrado com o filtro aplicado'}
            </div>
          )}

          {filteredItems.length === 0 && loading && (
            <div className="acomp-itens__empty-message">Carregando...</div>
          )}

          <div className="acomp-itens__cards-container">
            {filteredItems.map(item => (
              <ItemCard
                key={item.product_id}
                item={item}
                animationClass={getAnimationClass(item.product_id)}
              />
            ))}
          </div>
        </div>
      </div>
    </Dialog>
  )
}

function ItemCard({ item, animationClass }) {
  const percent = item.qte_total > 0 ? Math.min((item.qte_separada / item.qte_total) * 100, 100) : 0
  const percentFormatted = percent.toFixed(1)
  const qteDif = item.qte_separada - item.qte_total
  const isComplete = percent >= 100
  const progressBarClass = isComplete ? 'acomp-itens__progressbar--complete' : ''
  const bipParts = (item.last_bip || '').split(' ')
  const bipDate = bipParts[0] || ''
  const bipTime = bipParts[1] || ''
  const lastBip = [bipDate, bipTime].filter(Boolean).join(' ')
  const productDescription = [item.product_code, item.product_name].filter(Boolean).join(' - ')

  return (
    <div className={`acomp-itens__card ${animationClass}`}>
      {/* Info: [último bip coluna] | [código + nome coluna] */}
      <div className="acomp-itens__card-info">
        <div className="acomp-itens__card-info-bip">
          <span className="acomp-itens__card-last-bip-label">Último bip</span>
          <span className="acomp-itens__card-last-bip-value">{lastBip}</span>
        </div>
        <div className="acomp-itens__card-info-product">
          <div className="acomp-itens__card-product-label">Produto</div>
          <div className="acomp-itens__card-product-value" title={productDescription}>
            {productDescription}
          </div>
        </div>
      </div>

      {/* Progresso: rótulo acima da barra */}
      <div className="acomp-itens__card-progress">
        <span className="acomp-itens__progress-label">Separação</span>
        <div className="acomp-itens__progress-bar-container">
          <ProgressBar
            className={progressBarClass}
            value={percent}
            showValue={false}
            style={{ height: '18px', backgroundColor: '#e0e0e0' }}
          />
          <span className="acomp-itens__progress-value">{percentFormatted}%</span>
        </div>
      </div>

      {/* Stats */}
      <div className="acomp-itens__card-stats">
        <div className="acomp-itens__stat-item">
          <div className="acomp-itens__stat-valor">{item.qte_total}</div>
          <div className="acomp-itens__stat-label">Qte Total</div>
        </div>
        <div className="acomp-itens__stat-item">
          <div className="acomp-itens__stat-valor">{item.qte_separada}</div>
          <div className="acomp-itens__stat-label">Qte Separada</div>
        </div>
        <div className="acomp-itens__stat-item">
          <div
            className={`acomp-itens__stat-valor ${qteDif < 0 ? 'acomp-itens__stat-valor--negativo' : ''}`}
          >
            {qteDif > 0 ? 0 : qteDif}
          </div>
          <div className="acomp-itens__stat-label">Qte Dif</div>
        </div>
      </div>
    </div>
  )
}
